import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { SessionInfo } from '@oms/contracts';
import { useLogout, useSelectTenant } from '../../api/auth';
import { Button, ErrorState, StatusBadge } from '../../components';
import { safeNext } from '../../lib/navigation-helpers';
import { AuthCard } from '../../layout/PublicLayout';
import { BrandHeader } from './BrandHeader';

export function SelectWorkspacePage({ session }: { session: SessionInfo }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const select = useSelectTenant();
  const logout = useLogout();
  useEffect(() => {
    document.title = 'Choose a workspace · Zeriti';
  }, []);

  const choose = (tenantId: string) =>
    select.mutate(tenantId, { onSuccess: () => navigate(safeNext(params.get('next'), '/'), { replace: true }) });

  return (
    <AuthCard brand={<BrandHeader tenant={null} />}>
      <h1 style={{ fontSize: 20 }}>Choose a workspace</h1>
      <p className="muted" style={{ margin: 0 }}>
        Signed in as {session.user.email}.
      </p>
      {select.error && <ErrorState error={select.error} title="Could not open the workspace" />}
      {session.memberships.length === 0 ? (
        <div className="notice notice-warning">Your account is not an active member of any workspace. Ask your administrator for an invitation.</div>
      ) : (
        <ul className="workspace-list" aria-label="Workspaces">
          {session.memberships.map((m) => {
            const current = session.tenant?.tenantId === m.tenantId;
            return (
              <li key={m.tenantId}>
                <Button onClick={() => choose(m.tenantId)} loading={select.isPending && select.variables === m.tenantId} disabled={select.isPending} aria-current={current || undefined}>
                  <span>
                    {m.displayName} <span className="muted small">({m.slug})</span>
                  </span>
                  <span className="row">
                    {current && <StatusBadge status="current" />}
                    {m.status !== 'active' && <StatusBadge status={m.status} />}
                  </span>
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      <div className="auth-footer">
        <Button variant="link" onClick={() => logout.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) })}>
          Sign out
        </Button>
      </div>
    </AuthCard>
  );
}
